import React from "react";
import { formatTime } from "../../utilities/time";
import moment from "moment";

interface TimelineRulerProps {
    streamStart: Date;
    streamEnd: Date;
  duration: number;
}

// Utility function for picking marker intervals based on the timeline's duration
function getIntervals(duration: number) {
  /*
    Adjust these thresholds and values to suit your needs. 
    For example:
    
    - If duration <= 1 minute, very frequent ticks
    - If duration up to 5 minutes, less frequent
    - If duration up to 30 minutes, 1 hour, 2 hours, etc. 
  */

  if (duration <= 60) {
    // Up to 1 minute
    return { interval: 5, majorInterval: 15 };
  } else if (duration <= 5 * 60) {
    // Up to 5 minutes
    return { interval: 10, majorInterval: 60 };
  } else if (duration <= 30 * 60) {
    // Up to 30 minutes
    return { interval: 30, majorInterval: 60 };
  } else if (duration <= 60 * 60) {
    // Up to 1 hour
    return { interval: 60, majorInterval: 300 };
  } else if (duration <= 3 * 60 * 60) {
    // Up to 3 hours
    return { interval: 300, majorInterval: 900 };
  } else if (duration <= 6 * 60 * 60) {
    // Up to 6 hours
    return { interval: 600, majorInterval: 1800 };
  } else if (duration <= 12 * 60 * 60) {
    // Up to 12 hours
    return { interval: 900, majorInterval: 3600 };
  } else if (duration <= 24 * 60 * 60) {
    // Up to 24 hours
    return { interval: 1800, majorInterval: 7200 };
  } else {
    // More than 24 hours
    return { interval: 3600, majorInterval: 14400 };
  }
}

export function TimelineRuler({ duration, streamStart, streamEnd }: TimelineRulerProps) {
  // If the duration is zero or negative, nothing to render
  if (duration <= 0) {
    return null;
  }

  // Dynamically pick intervals based on the duration
  const { interval, majorInterval } = getIntervals(duration);

  // Precompute markers (using React.useMemo if you like)
  const markers = [];
  for (let time = 0; time <= duration + 0.00001; time += interval) {
    const clampedTime = Math.min(time, duration);
    const isMajor = clampedTime % majorInterval === 0;
    markers.push({ time: clampedTime, isMajor });
    if (clampedTime >= duration) break;
  }

  return (
    <div className="tw-absolute tw-top-0 tw-left-0 tw-right-0">
      {markers.map(({ time, isMajor }) => (
        <div
          key={time}
          className="tw-absolute"
          style={{ left: `${(time / duration) * 100}%` }}
          aria-hidden="true"
          role="presentation"
        >
          <div
            className={`tw-absolute tw-w-px ${
              isMajor ? "tw-h-3 -tw-top-3" : "tw-h-2 -tw-top-2"
            }`}
            style={{
              background: isMajor ? "#6b7280" : "#d1d5db",
            }}
          />
          {isMajor && (
            <div className="tw-absolute -tw-top-7 -tw-translate-x-1/2 tw-text-xs tw-text-gray-500 tw-whitespace-nowrap">
              {moment(streamStart).add(time, 'seconds').format('h:mm A')}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
